import { useEffect, useState } from "react";
import { PageHero, SectionEyebrow } from "@/components/Primitives";
import SEO from "@/lib/seo";
import ReactMarkdown from "react-markdown";
import MDEditor from "@uiw/react-md-editor";
import {
  api,
  adminListUsers, adminListInquiries, adminListMemberships, adminListPayments,
  adminListNewsletter, adminListDemos,
  adminCreatePub, adminUpdatePub, adminDeletePub, fetchPublications,
  adminCreateInsight, adminUpdateInsight, adminDeleteInsight, fetchInsights,
  adminCreateCase, adminUpdateCase, adminDeleteCase, fetchCaseStudies,
  formatApiError,
} from "@/lib/api";
import { Edit3, Plus, Trash2, X, Users, Mail, CreditCard, MessageSquare, FileText, BookOpen, Award, Upload, Inbox, GraduationCap, Eye, Check } from "lucide-react";
import { toast } from "sonner";

const SECTIONS = [
  { id: "overview", label: "Overview", icon: Users },
  { id: "publications", label: "Publications", icon: BookOpen },
  { id: "insights", label: "Insights", icon: FileText },
  { id: "case-studies", label: "Case studies", icon: Award },
  { id: "users", label: "Users", icon: Users },
  { id: "inquiries", label: "Inquiries", icon: MessageSquare },
  { id: "memberships", label: "Memberships", icon: Mail },
  { id: "payments", label: "Payments", icon: CreditCard },
  { id: "enrolments", label: "Enrolments", icon: GraduationCap },
  { id: "submissions", label: "Submissions", icon: Inbox },
  { id: "email-outbox", label: "Email outbox", icon: Mail },
];

export default function Admin() {
  const [section, setSection] = useState("overview");

  return (
    <div data-testid="admin-page">
      <SEO title="Admin · The Cleanability Institute" path="/admin" />
      <PageHero testId="admin-hero" eyebrow="Administration" title="Institute control panel." lead="Manage publications, insights, case studies, users, payments and outbound communications from a single console." />

      <section className="container-x pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <aside className="lg:col-span-3">
            <div className="rounded-2xl border border-border bg-card p-3 sticky top-24">
              {SECTIONS.map((s) => (
                <button key={s.id} onClick={() => setSection(s.id)} data-testid={`admin-nav-${s.id}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    section === s.id ? "bg-foreground text-background" : "hover:bg-secondary text-foreground/80"
                  }`}>
                  <s.icon className="h-4 w-4" /> {s.label}
                </button>
              ))}
            </div>
          </aside>
          <main className="lg:col-span-9">
            {section === "overview" && <Overview />}
            {section === "publications" && <CrudList kind="publications" />}
            {section === "insights" && <CrudList kind="insights" />}
            {section === "case-studies" && <CrudList kind="case-studies" />}
            {section === "users" && <ReadOnly loader={adminListUsers} keys={["email", "name", "role", "tier", "created_at"]} testId="admin-users" />}
            {section === "inquiries" && <ReadOnly loader={adminListInquiries} keys={["name", "email", "inquiry_type", "organisation", "message", "created_at"]} testId="admin-inquiries" />}
            {section === "memberships" && <ReadOnly loader={adminListMemberships} keys={["name", "email", "tier", "country", "status", "created_at"]} testId="admin-memberships" />}
            {section === "payments" && <ReadOnly loader={adminListPayments} keys={["email", "kind", "tier", "course_code", "amount", "currency", "payment_status", "created_at"]} testId="admin-payments" />}
            {section === "enrolments" && <ReadOnly loader={() => api.get("/admin/enrolments").then((r) => r.data)} keys={["user_id", "course_code", "course_title", "progress_pct", "status", "enrolled_at"]} testId="admin-enrolments" />}
            {section === "submissions" && <SubmissionsAdmin />}
            {section === "email-outbox" && <EmailOutbox />}
          </main>
        </div>
      </section>
    </div>
  );
}

function Overview() {
  const [counts, setCounts] = useState({});
  useEffect(() => {
    Promise.all([
      adminListUsers(), adminListInquiries(), adminListMemberships(),
      adminListPayments(), adminListNewsletter(), adminListDemos(),
      api.get("/admin/enrolments").then((r) => r.data),
      api.get("/admin/email-outbox").then((r) => r.data),
    ]).then(([u, i, m, p, n, d, e, o]) =>
      setCounts({ users: u.length, inquiries: i.length, memberships: m.length, payments: p.length, newsletter: n.length, demos: d.length, enrolments: e.length, outbox: o.length })
    ).catch(() => {});
  }, []);
  const items = [
    ["Users", counts.users || 0, "users"],
    ["Inquiries", counts.inquiries || 0, "inquiries"],
    ["Membership apps", counts.memberships || 0, "memberships"],
    ["Payments", counts.payments || 0, "payments"],
    ["Newsletter", counts.newsletter || 0, "newsletter"],
    ["Demo requests", counts.demos || 0, "demos"],
    ["Enrolments", counts.enrolments || 0, "enrolments"],
    ["Email outbox", counts.outbox || 0, "outbox"],
  ];
  return (
    <div data-testid="admin-overview">
      <SectionEyebrow>Overview</SectionEyebrow>
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        {items.map(([l, v, k]) => (
          <div key={k} data-testid={`overview-${k}`} className="rounded-2xl border border-border bg-card p-6">
            <div className="font-mono-mini text-muted-foreground">{l}</div>
            <div className="mt-2 font-display text-4xl font-medium tracking-tight">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReadOnly({ loader, keys, testId }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { loader().then(setRows).catch(() => {}); }, [loader]);
  return (
    <div data-testid={testId}>
      <SectionEyebrow>{testId.replace("admin-", "")}</SectionEyebrow>
      <div className="mt-6 rounded-2xl border border-border bg-card overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground">No records yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/60">
                <tr>{keys.map((k) => <th key={k} className="text-left px-4 py-3 font-mono-mini text-muted-foreground">{k}</th>)}</tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id || r.session_id || i} className="border-t border-border">
                    {keys.map((k) => <td key={k} className="px-4 py-3 align-top text-foreground/90 max-w-[260px] truncate">{String(r[k] ?? "")}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function EmailOutbox() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/admin/email-outbox").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div data-testid="admin-email-outbox">
      <SectionEyebrow>Email outbox</SectionEyebrow>
      <p className="mt-2 text-xs text-muted-foreground">No RESEND_API_KEY configured — emails are stored here. Add the key to <code className="font-mono-mini">/app/backend/.env</code> to start sending.</p>
      <div className="mt-6 rounded-2xl border border-border bg-card overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground">No emails queued.</div>
        ) : (
          <div className="divide-y divide-border">
            {rows.map((m, i) => (
              <div key={m.id} data-testid={`outbox-row-${i}`} className="p-5 flex items-center justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="font-display text-sm font-medium truncate">{m.subject}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">to {m.to} · {m.template}</div>
                </div>
                <span className={`text-[10px] font-mono-mini uppercase px-2 py-1 rounded-md border ${m.status === "sent" ? "border-[hsl(var(--accent))] text-[hsl(var(--accent))]" : m.status === "failed" ? "border-destructive text-destructive" : "border-border text-muted-foreground"}`}>{m.status}</span>
                <button onClick={() => setOpen(m)} data-testid={`outbox-view-${i}`} className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-border hover:bg-secondary"><Eye className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
      {open && (
        <div className="fixed inset-0 z-50 flex" data-testid="outbox-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(null)} />
          <div className="relative ml-auto w-full max-w-xl bg-background border-l border-border overflow-y-auto">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <div>
                <div className="font-mono-mini text-muted-foreground">Email</div>
                <div className="font-display text-xl font-medium tracking-tight">{open.subject}</div>
              </div>
              <button onClick={() => setOpen(null)} className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-border"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-6 text-sm space-y-3">
              <div><span className="text-muted-foreground">To:</span> {open.to}</div>
              <div><span className="text-muted-foreground">From:</span> {open.from}</div>
              <div><span className="text-muted-foreground">Status:</span> {open.status}</div>
              <div className="mt-4 pt-4 border-t border-border" dangerouslySetInnerHTML={{ __html: open.body_html }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SubmissionsAdmin() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const reload = () => api.get("/admin/submissions").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { reload(); }, []);

  const approve = async (id) => {
    if (!window.confirm("Approve and publish this submission?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/submissions/${id}/approve`);
      toast.success(`Published at /insights/${data.slug}`);
      setOpen(null); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const reject = async (id) => {
    if (!window.confirm("Reject this submission?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/submissions/${id}/reject`);
      toast.success("Submission rejected and author notified.");
      setOpen(null); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-submissions">
      <SectionEyebrow>Editorial submissions</SectionEyebrow>
      <div className="mt-6 rounded-2xl border border-border bg-card overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground">No submissions yet.</div>
        ) : (
          <div className="divide-y divide-border">
            {rows.map((s, i) => (
              <div key={s.id} data-testid={`submission-row-${i}`} className="p-5 flex items-center justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="font-display text-sm font-medium truncate">{s.title}</div>
                  <div className="text-xs text-muted-foreground mt-0.5 truncate">{s.author_name} · {s.author_email} · {s.category}</div>
                </div>
                <span className={`text-[10px] font-mono-mini uppercase px-2 py-1 rounded-md border ${s.status === "approved" ? "border-[hsl(var(--accent))] text-[hsl(var(--accent))]" : s.status === "rejected" ? "border-destructive text-destructive" : "border-border text-muted-foreground"}`}>{s.status}</span>
                <button onClick={() => setOpen(s)} data-testid={`submission-view-${i}`} className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-border hover:bg-secondary"><Eye className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex" data-testid="submission-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(null)} />
          <div className="relative ml-auto w-full max-w-2xl bg-background border-l border-border overflow-y-auto">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <div className="min-w-0">
                <div className="font-mono-mini text-muted-foreground">{open.category} · {open.status}</div>
                <div className="font-display text-xl font-medium tracking-tight truncate">{open.title}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{open.author_name} · {open.author_email}{open.organisation ? ` · ${open.organisation}` : ""}</div>
              </div>
              <button onClick={() => setOpen(null)} className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-border"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-6 space-y-5 text-sm">
              <div>
                <div className="font-mono-mini text-muted-foreground">Standfirst</div>
                <p className="mt-2 font-serif-display text-lg leading-snug">{open.excerpt}</p>
              </div>
              <div className="pt-4 border-t border-border">
                <div className="font-mono-mini text-muted-foreground">Body</div>
                <div className="mt-3 prose-essay leading-relaxed text-foreground/85"><ReactMarkdown>{open.content}</ReactMarkdown></div>
              </div>
              {open.status === "pending" && (
                <div className="pt-4 border-t border-border flex flex-wrap gap-3">
                  <button onClick={() => approve(open.id)} disabled={busy} data-testid="submission-approve" className="inline-flex items-center gap-1.5 h-11 px-5 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90 disabled:opacity-60">
                    <Check className="h-3.5 w-3.5" /> Approve & publish
                  </button>
                  <button onClick={() => reject(open.id)} disabled={busy} data-testid="submission-reject" className="inline-flex items-center gap-1.5 h-11 px-5 rounded-md border border-border text-destructive text-sm font-medium hover:bg-secondary disabled:opacity-60">
                    <X className="h-3.5 w-3.5" /> Reject
                  </button>
                </div>
              )}
              {open.status === "approved" && open.published_slug && (
                <div className="pt-4 border-t border-border text-sm">
                  Published at <a href={`/insights/${open.published_slug}`} className="underline-link font-medium">/insights/{open.published_slug}</a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const CRUD_CONFIG = {
  publications: {
    label: "Publications", loader: fetchPublications,
    create: adminCreatePub, update: adminUpdatePub, remove: adminDeletePub,
    fields: [
      { name: "title", label: "Title" }, { name: "author", label: "Author" },
      { name: "year", label: "Year", type: "number" }, { name: "category", label: "Category" },
      { name: "type", label: "Type" },
      { name: "abstract", label: "Abstract", textarea: true, markdown: true },
      { name: "pages", label: "Pages", type: "number" }, { name: "doi", label: "DOI" },
      { name: "pdf_path", label: "PDF file", upload: "publication" },
    ],
    primary: "title",
  },
  insights: {
    label: "Insights", loader: fetchInsights,
    create: adminCreateInsight, update: adminUpdateInsight, remove: adminDeleteInsight,
    fields: [
      { name: "title", label: "Title" }, { name: "slug", label: "Slug" },
      { name: "author", label: "Author" }, { name: "category", label: "Category" },
      { name: "excerpt", label: "Excerpt", textarea: true },
      { name: "content", label: "Content (Markdown)", textarea: true, markdown: true, editor: "wysiwyg", rows: 12 },
      { name: "published_at", label: "Published (YYYY-MM-DD)" },
      { name: "read_minutes", label: "Read minutes", type: "number" },
    ],
    primary: "title",
  },
  "case-studies": {
    label: "Case studies", loader: fetchCaseStudies,
    create: adminCreateCase, update: adminUpdateCase, remove: adminDeleteCase,
    fields: [
      { name: "title", label: "Title" }, { name: "sector", label: "Sector" },
      { name: "location", label: "Location" }, { name: "year", label: "Year", type: "number" },
      { name: "ci_before", label: "CI before", type: "number" }, { name: "ci_after", label: "CI after", type: "number" },
      { name: "cost_reduction", label: "Cost reduction %", type: "number" }, { name: "hygiene_uplift", label: "Hygiene uplift %", type: "number" },
      { name: "summary", label: "Summary", textarea: true, markdown: true },
    ],
    primary: "title",
  },
};

function CrudList({ kind }) {
  const cfg = CRUD_CONFIG[kind];
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const reload = () => cfg.loader().then(setRows).catch(() => {});
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [kind]);

  const save = async (data) => {
    try {
      const payload = { ...data };
      cfg.fields.forEach((f) => {
        if (f.type === "number" && payload[f.name] != null && payload[f.name] !== "") {
          payload[f.name] = Number(payload[f.name]);
        }
      });
      if (kind === "publications" && !payload.tags) payload.tags = [];
      if (editing.id) await cfg.update(editing.id, payload);
      else await cfg.create(payload);
      toast.success("Saved.");
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row[cfg.primary]}"?`)) return;
    try { await cfg.remove(row.id); toast.success("Deleted."); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid={`admin-crud-${kind}`}>
      <div className="flex items-center justify-between">
        <SectionEyebrow>{cfg.label}</SectionEyebrow>
        <button onClick={() => setEditing({})} data-testid={`admin-new-${kind}`} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90">
          <Plus className="h-3.5 w-3.5" /> New
        </button>
      </div>

      <div className="mt-6 rounded-2xl border border-border bg-card overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground">Nothing here yet.</div>
        ) : (
          <div className="divide-y divide-border">
            {rows.map((row, i) => (
              <div key={row.id} data-testid={`admin-row-${kind}-${i}`} className="p-5 flex items-center justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="font-display font-medium truncate">{row[cfg.primary]}</div>
                  <div className="text-xs text-muted-foreground truncate">{row.author || row.sector || row.category || ""}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setEditing(row)} data-testid={`admin-edit-${kind}-${i}`} className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-border hover:bg-secondary">
                    <Edit3 className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => remove(row)} data-testid={`admin-delete-${kind}-${i}`} className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-border hover:bg-secondary text-destructive">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing !== null && (
        <EditDrawer initial={editing} fields={cfg.fields} onClose={() => setEditing(null)} onSave={save} label={cfg.label} kind={kind} />
      )}
    </div>
  );
}

function EditDrawer({ initial, fields, onClose, onSave, label, kind }) {
  const [data, setData] = useState(() => {
    const d = {}; fields.forEach((f) => { d[f.name] = initial?.[f.name] ?? ""; }); return d;
  });
  const [preview, setPreview] = useState(null);

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" data-testid={`admin-drawer-${kind}`}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-background border-l border-border overflow-y-auto">
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div>
            <div className="font-mono-mini text-muted-foreground">{initial?.id ? "Edit" : "New"}</div>
            <div className="font-display text-xl font-medium tracking-tight">{label}</div>
          </div>
          <button onClick={onClose} data-testid="admin-drawer-close" className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-border hover:bg-secondary"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onSave({ ...initial, ...data }); }} data-testid={`admin-form-${kind}`} className="p-6 space-y-4">
          {fields.map((f) => (
            <FieldRow key={f.name} f={f} data={data} setData={setData} kind={kind} preview={preview} setPreview={setPreview} />
          ))}
          <button type="submit" data-testid={`admin-save-${kind}`} className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90">Save</button>
        </form>
      </div>
    </div>
  );
}

function FieldRow({ f, data, setData, kind, preview, setPreview }) {
  const [uploading, setUploading] = useState(false);

  if (f.upload) {
    return (
      <div>
        <div className="text-xs font-mono-mini text-muted-foreground">{f.label}</div>
        <div className="mt-2 flex items-center gap-3">
          <label className={`inline-flex items-center gap-2 h-10 px-4 rounded-md border border-border bg-card text-sm cursor-pointer hover:border-[hsl(var(--accent))] ${uploading ? "opacity-60" : ""}`} data-testid={`admin-upload-${kind}-${f.name}`}>
            <Upload className="h-3.5 w-3.5" /> {uploading ? "Uploading…" : "Upload file"}
            <input type="file" className="hidden" accept=".pdf,image/*"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setUploading(true);
                try {
                  const fd = new FormData(); fd.append("file", file); fd.append("kind", f.upload);
                  const { data: result } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                  setData({ ...data, [f.name]: result.path });
                  toast.success(`Uploaded ${result.filename}`);
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Upload failed");
                } finally { setUploading(false); }
              }} />
          </label>
          {data[f.name] && <span className="text-xs text-muted-foreground truncate flex-1">{data[f.name]}</span>}
        </div>
      </div>
    );
  }

  if (f.textarea) {
    const isPreviewing = preview === f.name;
    return (
      <div>
        <div className="flex items-center justify-between">
          <div className="text-xs font-mono-mini text-muted-foreground">{f.label}</div>
          {f.markdown && (
            <button type="button" onClick={() => setPreview(isPreviewing ? null : f.name)} data-testid={`admin-preview-${kind}-${f.name}`} className="text-[10px] font-mono-mini text-[hsl(var(--accent))] hover:underline">
              {isPreviewing ? "Edit" : "Preview"}
            </button>
          )}
        </div>
        {isPreviewing ? (
          <div className="mt-2 min-h-[180px] px-4 py-3 rounded-md border border-border bg-card text-sm leading-relaxed">
            <ReactMarkdown>{data[f.name] || "*Nothing to preview yet*"}</ReactMarkdown>
          </div>
        ) : (
          <textarea
            data-testid={`admin-input-${kind}-${f.name}`}
            value={data[f.name]}
            onChange={(e) => setData({ ...data, [f.name]: e.target.value })}
            rows={f.rows || 5}
            className="mt-2 w-full px-3 py-2 rounded-md border border-border bg-card text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--accent))] font-mono"
          />
        )}
        {f.markdown && !isPreviewing && f.editor !== "wysiwyg" && (
          <p className="mt-1 text-[10px] text-muted-foreground">Supports Markdown: # headings, **bold**, *italic*, links, lists, &gt; quotes.</p>
        )}
        {f.markdown && f.editor === "wysiwyg" && !isPreviewing && (
          <div className="mt-2" data-testid={`admin-mdeditor-${kind}-${f.name}`}>
            <MDEditor value={data[f.name] || ""} onChange={(v) => setData({ ...data, [f.name]: v || "" })} height={360} preview="edit" />
          </div>
        )}
      </div>
    );
  }

  return (
    <label className="block">
      <div className="text-xs font-mono-mini text-muted-foreground">{f.label}</div>
      <input
        data-testid={`admin-input-${kind}-${f.name}`}
        type={f.type || "text"}
        value={data[f.name]}
        onChange={(e) => setData({ ...data, [f.name]: e.target.value })}
        className="mt-2 w-full h-10 px-3 rounded-md border border-border bg-card text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--accent))]"
      />
    </label>
  );
}
